import { motion } from 'framer-motion';
import AdminSidebar from '@/pages/admin/components/AdminSidebar';
import AdminTopBar from '@/pages/admin/components/AdminTopBar';
import PageHeader from '@/components/feature/PageHeader';
import AccountingKPIs from './components/AccountingKPIs';
import AccountingFilters from './components/AccountingFilters';
import AccountingTable from './components/AccountingTable';
import ExportPDFButton from './components/ExportPDFButton';
import { useAccounting } from '@/hooks/useAccounting';
import { theme } from '@/lib/theme';

export default function AccountingPage() {
  const { payments, kpis, loading, error, filters, updateFilters, refetch } = useAccounting();

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: theme.colors.bgBase }}>
      <AdminSidebar />

      <div className="flex-1 flex flex-col min-w-0">
        <AdminTopBar
          title="Contabilidad"
          subtitle="Registro financiero de ingresos reales"
        />

        <main className="flex-1 overflow-y-auto px-6 py-8">
          <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
            <div>
              <h1 className="text-xl font-bold text-white">Contabilidad</h1>
              <p className="text-sm mt-0.5" style={{ color: theme.colors.textMuted }}>
                Fuente única de verdad: todos los ingresos provienen de pagos registrados
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <ExportPDFButton
                payments={payments}
                kpis={kpis}
                filters={filters}
                disabled={loading}
              />
              <button
                onClick={refetch}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap disabled:opacity-40"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  color: theme.colors.textSecondary,
                  border: `1px solid ${theme.colors.border}`,
                }}
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className={`ri-refresh-line text-xs ${loading ? 'animate-spin' : ''}`} />
                </div>
                Actualizar
              </button>
            </div>
          </div>

          {/* Error banner */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-3 rounded-xl px-5 py-4 mb-6 border"
              style={{
                backgroundColor: theme.colors.dangerMuted,
                borderColor: 'rgba(248,113,113,0.2)',
              }}
            >
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-red-400 text-base" />
              </div>
              <p className="text-red-400 text-sm flex-1">{error}</p>
              <button
                onClick={refetch}
                className="text-red-400 hover:text-red-300 text-xs underline cursor-pointer whitespace-nowrap"
              >
                Reintentar
              </button>
            </motion.div>
          )}

          {/* Business logic note */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="flex items-start gap-3 rounded-xl px-5 py-4 mb-6 border"
            style={{
              backgroundColor: 'rgba(52,211,153,0.04)',
              borderColor: 'rgba(52,211,153,0.12)',
            }}
          >
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-information-line text-sm text-emerald-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs leading-relaxed" style={{ color: 'rgba(52,211,153,0.75)' }}>
                <strong className="text-emerald-400">Lógica de negocio:</strong>{' '}
                Los ingresos se calculan exclusivamente desde la tabla de pagos.
                Un servicio completado genera dos registros: <em>anticipo</em> (Wompi) + <em>restante</em> (efectivo o transferencia).
                Un <em>no-show</em> solo genera el anticipo. Ningún valor es estimado o derivado.
              </p>
            </div>
          </motion.div>

          {/* KPI Cards */}
          <div className="mb-6">
            <AccountingKPIs kpis={kpis} loading={loading} />
          </div>

          {/* Revenue breakdown bar */}
          {!loading && kpis.totalRevenue > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="rounded-xl p-5 border mb-6"
              style={{ backgroundColor: theme.colors.bgSurface, borderColor: theme.colors.border }}
            >
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-white">Distribución de ingresos</p>
                <p className="text-xs" style={{ color: theme.colors.textMuted }}>
                  Anticipo vs. Restante
                </p>
              </div>
              <div className="h-3 rounded-full overflow-hidden flex gap-0.5" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                <motion.div
                  className="h-full rounded-l-full"
                  style={{ backgroundColor: theme.colors.primary }}
                  initial={{ width: 0 }}
                  animate={{ width: `${kpis.totalRevenue > 0 ? (kpis.advanceRevenue / kpis.totalRevenue) * 100 : 0}%` }}
                  transition={{ duration: 0.8, ease: 'easeOut', delay: 0.3 }}
                />
                <motion.div
                  className="h-full rounded-r-full"
                  style={{ backgroundColor: '#34d399' }}
                  initial={{ width: 0 }}
                  animate={{ width: `${kpis.totalRevenue > 0 ? (kpis.remainingRevenue / kpis.totalRevenue) * 100 : 0}%` }}
                  transition={{ duration: 0.8, ease: 'easeOut', delay: 0.4 }}
                />
              </div>
              <div className="flex items-center gap-6 mt-3">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: theme.colors.primary }} />
                  <span className="text-xs" style={{ color: theme.colors.textMuted }}>
                    Anticipos — {kpis.totalRevenue > 0 ? Math.round((kpis.advanceRevenue / kpis.totalRevenue) * 100) : 0}%
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-emerald-400" />
                  <span className="text-xs" style={{ color: theme.colors.textMuted }}>
                    Restantes — {kpis.totalRevenue > 0 ? Math.round((kpis.remainingRevenue / kpis.totalRevenue) * 100) : 0}%
                  </span>
                </div>
              </div>
            </motion.div>
          )}

          {/* Filters */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-5"
          >
            <AccountingFilters
              filters={filters}
              onUpdate={updateFilters}
              total={payments.length}
              loading={loading}
            />
          </motion.div>

          {/* Ledger table */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <AccountingTable payments={payments} loading={loading} />
          </motion.div>
        </main>
      </div>
    </div>
  );
}
